import React, { useState, useEffect } from 'react';

import { useRouter } from 'next/router';

import { useSelector } from 'react-redux';

import { Button, Text } from '@BICE_Vida/components';

import styles from './Contactability.module.scss';

import { maxLengthRut } from '@/utils/constants';
import componentsId from '@/utils/componentsId';
import { formatRut, unformatRut } from '@/utils/format';
import { contactabilityFormTexts } from '@/utils/generalTexts';
import { contactabilityFormValidation } from '@/utils/validations';

import ContactabilityForm from '@/components/Home/Hero/ContactabilityForm';
import ContactabilityResult from '@/components/Home/Hero/ContactabilityResult';

import * as wizardSelectors from '@/redux/wizard/selectors';

import { useIsAssisted } from '@/src/customHooks';

import { useMutation } from '@apollo/client';
import sendMailContactExecutive from '@/graphQL/mutations/sendMailContactExecutive';
import { trackClick, clickActionsEvents, contactabilityEvents } from '@/utils/dataLayer/home';
import { trackEvent } from '@/utils/dataLayer/baseDataLayer';

interface ContactabilityProps {
  readonly onClose: () => void;
  readonly identifier: string;
}

function Contactability({ onClose, identifier }: ContactabilityProps) {
  const router = useRouter();
  const userData = useSelector(wizardSelectors.getContactabilityFormData);
  const isAssisted = useIsAssisted();
  const [sendMail, { loading: sendingMail }] = useMutation(sendMailContactExecutive);

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [availableInput, setAvailableInput] = useState({
    companyRut: false,
    companyName: false,
    workersNumber: false,
    contactName: false,
    email: false,
    phone: false,
    form: true,
  });

  const [contactabilityForm, setContactabilityForm] = useState({
    companyRut: userData.companyRut,
    companyName: userData.companyName,
    workersNumber: userData.workersNumber ?? '',
    contactName: userData.contactName ? userData.contactName : '',
    email: userData.email,
    phone: userData.phone ?? '',
  });

  const [formErrors, setFormErrors] = useState({
    companyRut: { hasError: false, feedback: '' },
    companyName: { hasError: false, feedback: '' },
    workersNumber: { hasError: false, feedback: '' },
    contactName: { hasError: false, feedback: '' },
    email: { hasError: false, feedback: '' },
    phone: { hasError: false, feedback: '' },
  });

  useEffect(() => {
    trackEvent({
      eventParams: {
        ...contactabilityEvents.formView,
      },
    });
  }, []);

  useEffect(() => {
    if (formStatus === 'success') {
      trackEvent({
        eventParams: {
          ...contactabilityEvents.successView,
        },
      });
    } else if (formStatus === 'error') {
      trackEvent({
        eventParams: {
          ...contactabilityEvents.errorView,
        },
      });
    }
  }, [formStatus]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = event.target;
    let { value } = event.target;

    if (id === componentsId.contactabilityForm.inputCompanyRut) {
      value = formatRut(unformatRut(value).slice(0, maxLengthRut));
    }

    setContactabilityForm((prev) => ({
      ...prev,
      [id]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [id]: {
        hasError: false,
        feedback: '',
      },
    }));
  };

  const handleBlur = async (event: any) => {
    const { id, value } = event.target;
    try {
      await contactabilityFormValidation.validateAsync({ [id]: value }, { convert: false });
      setFormErrors({
        ...formErrors,
        [id]: {
          hasError: false,
          feedback: '',
        },
      });
    } catch (error) {
      setFormErrors({
        ...formErrors,
        [id]: {
          hasError: true,
          feedback: (error as Error).message,
        },
      });
    }
  };

  const formatRutOnBlur = (event: any) => {
    if (event.target.value.length) {
      event.target.value = formatRut(unformatRut(event.target.value).slice(0, maxLengthRut));
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>, regEx: RegExp) => {
    if (!regEx.test(event.key)) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await contactabilityFormValidation.validateAsync(contactabilityForm, {
          convert: false,
          abortEarly: false,
        });
        setAvailableInput({
          companyRut: true,
          companyName: true,
          workersNumber: true,
          contactName: true,
          email: true,
          phone: true,
          form: true,
        });
      } catch (error) {
        const validationError = error as { inner?: { path: string }[] };
        const invalidFields = validationError.inner?.map((err) => err.path) ?? [];

        setAvailableInput({
          companyRut: !invalidFields.includes('companyRut'),
          companyName: !invalidFields.includes('companyName'),
          workersNumber: !invalidFields.includes('workersNumber'),
          contactName: !invalidFields.includes('contactName'),
          email: !invalidFields.includes('email'),
          phone: !invalidFields.includes('phone'),
          form: invalidFields.length === 0,
        });
      }
    })();
  }, [contactabilityForm, isAssisted]);

  const sendContactDataExecutive = async () => {
    trackClick({ action: clickActionsEvents.contactExecutiveSendData });

    try {
      await contactabilityFormValidation.validateAsync(contactabilityForm, {
        convert: false,
        abortEarly: false,
      });
    } catch (error) {
      const validationError = error as { details?: { path: string[]; message: string }[] };
      const newErrors = { ...formErrors };

      validationError.details?.forEach((detail) => {
        const field = detail.path[0];
        if (field && field in newErrors) {
          newErrors[field as keyof typeof newErrors] = {
            hasError: true,
            feedback: detail.message,
          };
        }
      });

      setFormErrors(newErrors);
      return;
    }

    const hasAnyError = Object.values(formErrors).some((field) => field.hasError);
    if (hasAnyError) {
      return;
    }

    try {
      const response = await sendMail({
        variables: {
          input: {
            company_rut: contactabilityForm.companyRut,
            company_name: contactabilityForm.companyName,
            workers: Number(contactabilityForm.workersNumber),
            name: contactabilityForm.contactName,
            lastname: '',
            email: contactabilityForm.email,
            phone: Number(contactabilityForm.phone.replace(/ /g, '')),
          },
        },
      });

      const result = response?.data?.sendMailContactExecutive;

      if (result?.body?.data && !result?.body?.error) {
        setFormStatus('success');
      } else {
        setFormStatus('error');
      }
    } catch (err) {
      console.error('Error:', err);
      setFormStatus('error');
    }
  };

  return (
    <div className={styles.contact_form_wrapper}>
      {formStatus !== 'idle' ? (
        <ContactabilityResult status={formStatus} onClose={onClose} />
      ) : (
        <>
          <div className={styles.title_text_box}>
            <Text tag="h1" color="primary" className={styles.contact_form_title}>
              {contactabilityFormTexts.title}
            </Text>
            <Text tag="h2" color="primary" className={styles.contact_form_subtitle}>
              {contactabilityFormTexts.subtitle}
            </Text>
          </div>
          <div className={styles.contact_form_content}>
            <ContactabilityForm
              companyRut={contactabilityForm.companyRut}
              companyName={contactabilityForm.companyName}
              workersNumber={contactabilityForm.workersNumber}
              contactName={contactabilityForm.contactName}
              email={contactabilityForm.email}
              phone={contactabilityForm.phone}
              formErrors={formErrors}
              loading={false}
              handleChange={handleChange}
              handleBlur={handleBlur}
              formatRutOnBlur={formatRutOnBlur}
              handleKeyPress={handleKeyPress}
              formSubmitted={formStatus !== 'idle'}
            />
            <Button
              className={styles.btn_send_contact_data}
              id={`${identifier}_header_btn_contact_executive`}
              text="Enviar datos"
              size="large"
              onClick={sendContactDataExecutive}
              aria-label={'header_btn_send_contact_executive'}
              variant="primary"
              disabled={!availableInput.form || sendingMail || formStatus !== 'idle'}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default Contactability;
